import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// Toast Context
const ToastContext = createContext();

// Toast Provider
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const addToast = useCallback((message, type = 'info', duration = 4000) => {
        const id = Date.now() + Math.random();
        const toast = { id, message, type, duration };

        setToasts(prev => [...prev, toast]);

        // Auto remove toast
        setTimeout(() => {
            removeToast(id);
        }, duration);

        return id;
    }, [removeToast]);

    const showSuccess = useCallback((message, duration) => addToast(message, 'success', duration), [addToast]);
    const showError = useCallback((message, duration) => addToast(message, 'error', duration), [addToast]);
    const showWarning = useCallback((message, duration) => addToast(message, 'warning', duration), [addToast]);
    const showInfo = useCallback((message, duration) => addToast(message, 'info', duration), [addToast]);

    const contextValue = useMemo(() => ({
        addToast,
        removeToast,
        showSuccess,
        showError,
        showWarning,
        showInfo
    }), [addToast, removeToast, showSuccess, showError, showWarning, showInfo]);

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </ToastContext.Provider>
    );
};

// Toast Hook
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

// Toast Icons
const getIcon = (type) => {
    const icons = {
        success: (
            <svg className="w-5 h-5 text-success-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
        ),
        error: (
            <svg className="w-5 h-5 text-error-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
        ),
        warning: (
            <svg className="w-5 h-5 text-warning-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
        ),
        info: (
            <svg className="w-5 h-5 text-info-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
        )
    };
    return icons[type] || icons.info;
};

// Individual Toast Component
const Toast = ({ toast, onRemove }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);

    useEffect(() => {
        // Animate in
        const timer = setTimeout(() => setIsVisible(true), 10);
        return () => clearTimeout(timer);
    }, []);

    const handleRemove = () => {
        setIsLeaving(true);
        setTimeout(() => onRemove(toast.id), 300);
    };

    const getToastStyles = (type) => {
        const styles = {
            success: 'bg-success-50 border-success-200 text-success-800',
            error: 'bg-error-50 border-error-200 text-error-800',
            warning: 'bg-warning-50 border-warning-200 text-warning-800',
            info: 'bg-info-50 border-info-200 text-info-800'
        };
        return styles[type] || styles.info;
    };

    return (
        <div
            className={`
                max-w-sm w-full shadow-lg rounded-lg pointer-events-auto border
                transform transition-all duration-300 ease-in-out
                ${getToastStyles(toast.type)}
                ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
                ${isLeaving ? 'scale-95' : 'scale-100'}
            `}
        >
            <div className="p-4">
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        {getIcon(toast.type)}
                    </div>
                    <div className="ml-3 w-0 flex-1">
                        <p className="text-sm font-medium">
                            {toast.message}
                        </p>
                    </div>
                    <div className="ml-4 flex-shrink-0 flex">
                        <button
                            className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none"
                            onClick={handleRemove}
                        >
                            <span className="sr-only">Close</span>
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Toast Container
const ToastContainer = ({ toasts, onRemove }) => {
    if (toasts.length === 0) return null;

    return (
        <div
            aria-live="assertive"
            className="fixed inset-0 flex items-end justify-center px-4 py-6 pointer-events-none sm:p-6 sm:items-start sm:justify-end z-toast"
        >
            <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
                {toasts.map(toast => (
                    <Toast key={toast.id} toast={toast} onRemove={onRemove} />
                ))}
            </div>
        </div>
    );
};

// Loading Overlay Component
export const LoadingOverlay = ({ isVisible, message = "处理中..." }) => {
    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-loading-overlay flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
                <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                    <div className="text-gray-700 font-medium">{message}</div>
                </div>
            </div>
        </div>
    );
};

// Confirmation Dialog Component
export const ConfirmDialog = ({
    isOpen,
    onClose,
    onConfirm,
    title = "操作確認",
    message = "この操作を実行してもよろしいですか？",
    confirmText = "確認",
    cancelText = "キャンセル",
    type = "warning" // success, warning, error, info
}) => {
    if (!isOpen) return null;

    const getColorStyles = (type) => {
        const styles = {
            success: {
                icon: 'text-success-600',
                button: 'btn-success'
            },
            warning: {
                icon: 'text-warning-600',
                button: 'btn-warning'
            },
            error: {
                icon: 'text-error-600',
                button: 'btn-error'
            },
            info: {
                icon: 'text-info-600',
                button: 'btn-primary'
            }
        };
        return styles[type] || styles.warning;
    };

    const colorStyles = getColorStyles(type);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-modal-backdrop flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                <div className="p-6">
                    <div className="flex items-center">
                        <div className={`flex-shrink-0 ${colorStyles.icon}`}>
                            {getIcon(type)}
                        </div>
                        <div className="ml-3">
                            <h3 className="text-lg font-medium text-gray-900">
                                {title}
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                    {message}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
                    <button
                        type="button"
                        className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        onClick={onClose}
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${type === 'success' ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500' :
                            type === 'warning' ? 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500' :
                                type === 'error' ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500' :
                                    'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
                            }`}
                        onClick={onConfirm}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};
